import { Maximize2, Minimize2, X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode, type KeyboardEventHandler } from 'react';

const scrollLocks = new Set<symbol>();
const imageFullscreenEntry = () => (window.history.state as { skrepkaImageFullscreen?: string } | null)?.skrepkaImageFullscreen;

interface ModalProps {
  title: ReactNode;
  children: ReactNode;
  onClose: () => void;
  size?: 'small' | 'medium' | 'large' | 'wide';
  actions?: ReactNode;
  headerToolbar?: ReactNode;
  className?: string;
  onKeyDown?: KeyboardEventHandler<HTMLElement>;
  immersiveFullscreen?: boolean;
}

export function Modal({ title, children, onClose, size = 'medium', actions, headerToolbar, className = '', onKeyDown, immersiveFullscreen = false }: ModalProps) {
  const titleId = useId();
  const [expanded, setExpanded] = useState(false);
  const expandedRef = useRef(false);
  const ownsFullscreen = useRef(false);
  const ownsOrientation = useRef(false);
  const fullscreenHistory = useRef<string | null>(null);
  const releaseHistory = () => {
    const entry = fullscreenHistory.current;
    fullscreenHistory.current = null;
    if (entry && imageFullscreenEntry() === entry) window.history.back();
  };
  const unlockOrientation = () => {
    if (ownsOrientation.current) { ownsOrientation.current = false; screen.orientation?.unlock?.(); }
  };
  const mounted = useRef(true);
  const restore = () => {
    expandedRef.current = false; setExpanded(false); unlockOrientation();
    releaseHistory();
    if (ownsFullscreen.current && document.fullscreenElement === document.documentElement) {
      ownsFullscreen.current = false;
      void document.exitFullscreen().catch(() => {});
    }
  };
  const restoreRef = useRef(restore);
  useEffect(() => { restoreRef.current = restore; });
  const toggleExpanded = () => {
    if (expandedRef.current) { restore(); return; }
    expandedRef.current = true; setExpanded(true);
    if (immersiveFullscreen) {
      const entry = crypto.randomUUID();
      window.history.pushState({ ...window.history.state, skrepkaImageFullscreen: entry }, '', window.location.href);
      fullscreenHistory.current = entry;
    }
    // Fullscreen the document so nested frame editors remain visible.
    // Unsupported/denied native fullscreen still gets the full viewport.
    if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
      ownsFullscreen.current = true;
      void document.documentElement.requestFullscreen().then(async () => {
        const orientation = screen.orientation as ScreenOrientation & { lock?: (value: string) => Promise<void> };
        if (mounted.current && expandedRef.current && className.split(' ').includes('video-review') && window.matchMedia('(pointer:coarse)').matches && orientation?.lock) {
          try {
            await orientation.lock('landscape'); ownsOrientation.current = true;
            if (!mounted.current || !expandedRef.current) unlockOrientation();
          } catch { /* Rotation can also be controlled by the device. */ }
        }
        if (!mounted.current || !expandedRef.current) {
          ownsFullscreen.current = false;
          if (document.fullscreenElement === document.documentElement) void document.exitFullscreen().catch(() => {});
        }
      }).catch(() => { ownsFullscreen.current = false; });
    }
  };
  useEffect(() => {
    mounted.current = true;
    const changed = () => {
      if (ownsFullscreen.current && !document.fullscreenElement) {
        ownsFullscreen.current = false; restoreRef.current();
      }
    };
    const back = () => {
      const entry = fullscreenHistory.current;
      if (entry && imageFullscreenEntry() !== entry) {
        fullscreenHistory.current = null;
        restoreRef.current();
      }
    };
    document.addEventListener('fullscreenchange', changed);
    window.addEventListener('popstate', back);
    return () => {
      mounted.current = false; unlockOrientation();
      document.removeEventListener('fullscreenchange', changed);
      window.removeEventListener('popstate', back);
      releaseHistory();
      if (ownsFullscreen.current && document.fullscreenElement === document.documentElement) {
        ownsFullscreen.current = false;
        void document.exitFullscreen().catch(() => {});
      }
    };
  }, []);
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const scrollLock = Symbol('modal');
    scrollLocks.add(scrollLock);
    const isTop = () => [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')].at(-1) === dialogRef.current;
    if (!dialogRef.current?.contains(document.activeElement)) dialogRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isTop()) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); if (expandedRef.current) restoreRef.current(); else closeRef.current(); }
      if (event.key === 'Tab' && dialogRef.current) {
        const nodes = [...dialogRef.current.querySelectorAll<HTMLElement>('button:not(:disabled),a[href],input:not(:disabled):not([hidden]),textarea:not(:disabled),select:not(:disabled),[tabindex="0"],[contenteditable="true"]')].filter(el => el.getClientRects().length);
        const first = nodes[0], last = nodes.at(-1);
        if (!first || !last) { event.preventDefault(); return; }
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      scrollLocks.delete(scrollLock);
      if (scrollLocks.size === 0) document.body.classList.remove('modal-open');
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, []);

  return (
    <div className={`modal-backdrop${expanded ? ' modal-backdrop--expanded' : ''}`} role="presentation" onMouseDown={onClose}>
      <section
        aria-labelledby={titleId}
        aria-modal="true"
        className={`modal modal--${size}${expanded ? ' modal--expanded' : ''}${expanded && immersiveFullscreen ? ' modal--immersive-image' : ''} ${className}`.trim()}
        role="dialog"
        ref={dialogRef}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal__header">
          <h2 id={titleId}>{title}</h2>
          {headerToolbar}
          <div className="modal__window-actions">
          <button aria-label={expanded ? 'Вернуть размер окна' : 'На весь экран'} title={expanded ? 'Вернуть размер окна' : 'На весь экран'} aria-pressed={expanded} className="icon-button" onClick={toggleExpanded} type="button">
            {expanded ? <Minimize2 size={22}/> : <Maximize2 size={22}/>}
          </button>
          <button aria-label="Закрыть" className="icon-button" onClick={onClose} type="button">
            <X size={20} />
          </button>
          </div>
        </header>
        <div className="modal__body">{children}</div>
        {actions ? <footer className="modal__actions">{actions}</footer> : null}
      </section>
    </div>
  );
}
